import { getPlatform } from '@/lib/platform';

/**
 * Tokens of the native app (VOL-14 lot 5). The access token lives in memory
 * only (15 min); the refresh token, which outlives the app, lives in the
 * device's secure storage. The website never comes here: its session stays in
 * httpOnly cookies the page cannot read.
 */

const REFRESH_TOKEN_KEY = 'vsp.native.refreshToken';

/** Body of `/auth/native/login` and `/auth/native/refresh`. */
export interface NativeTokenResponse<TUser> {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresIn: number;
    refreshTokenExpiresAt: string;
    user: TUser;
}

export function isNativeTokenResponse(body: unknown): body is { accessToken: string; refreshToken: string } {
    return (
        typeof body === 'object' &&
        body !== null &&
        'accessToken' in body &&
        typeof body.accessToken === 'string' &&
        'refreshToken' in body &&
        typeof body.refreshToken === 'string'
    );
}

let accessToken: string | null = null;

export function usesBearerAuth(): boolean {
    return getPlatform().authTransport === 'bearer';
}

export function getNativeAccessToken(): string | null {
    return accessToken;
}

export function getNativeRefreshToken(): Promise<string | null> {
    return getPlatform().secureStorage.get(REFRESH_TOKEN_KEY);
}

export async function saveNativeTokens(tokens: { accessToken: string; refreshToken: string }): Promise<void> {
    // The server has already rotated: keep the new access token even if the
    // keychain write below fails, so the coach is not cut off before it expires.
    accessToken = tokens.accessToken;
    await getPlatform().secureStorage.set(REFRESH_TOKEN_KEY, tokens.refreshToken);
}

export async function clearNativeTokens(): Promise<void> {
    accessToken = null;
    await getPlatform().secureStorage.remove(REFRESH_TOKEN_KEY);
}

/** Tests only. */
export function __resetNativeSession(): void {
    accessToken = null;
}
